import { useEffect, useState, useRef } from 'react';
import { useFriendStore } from '../../stores/friendStore.js';
import { usePresenceStore } from '../../stores/presenceStore.js';
import { useModalStore } from '../../stores/modalStore.js';

const STATUS_COLOR = { online: '#3ba55c', idle: '#faa61a', dnd: '#ed4245', offline: '#747f8d' };
const STATUS_LABEL = { online: 'Online', idle: 'Idle', dnd: 'Do Not Disturb', offline: 'Offline' };

function Avatar({ name, avatar, size = 28, gradient = 'linear-gradient(135deg,#7c3aed,#4f46e5)' }) {
  const initial = (name || '?')[0].toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
      {avatar || initial}
    </div>
  );
}

function StatusDot({ status, size = 8 }) {
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', background: STATUS_COLOR[status] || STATUS_COLOR.offline, border: '1.5px solid rgba(8,8,10,0.95)', display: 'inline-block', flexShrink: 0 }} />
  );
}

// ── Relative timestamp ────────────────────────────────────────────────────────
function useRelativeTime(ts) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    const update = () => {
      if (!ts) { setLabel(''); return; }
      const diff = Date.now() - ts;
      if (diff < 60000)       setLabel('just now');
      else if (diff < 3600000) setLabel(`${Math.floor(diff / 60000)}m`);
      else                     setLabel(`${Math.floor(diff / 3600000)}h`);
    };
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, [ts]);
  return label;
}

// ── Progress bar (for Spotify / music) ───────────────────────────────────────
function ProgressBar({ elapsed, duration }) {
  const [current, setCurrent] = useState(elapsed || 0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    setCurrent(elapsed || 0);
    startRef.current = Date.now();
    const id = setInterval(() => {
      const newVal = (elapsed || 0) + Math.floor((Date.now() - startRef.current) / 1000);
      setCurrent(Math.min(newVal, duration || newVal));
    }, 1000);
    return () => clearInterval(id);
  }, [elapsed, duration]);

  const pct = duration > 0 ? Math.min((current / duration) * 100, 100) : 0;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden', marginBottom: 3 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#1db954', borderRadius: 2, transition: 'width 1s linear' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>
        <span>{fmt(current)}</span>
        {duration > 0 && <span>{fmt(duration)}</span>}
      </div>
    </div>
  );
}

// ── Rich activity card ────────────────────────────────────────────────────────
function ActivityCard({ f, onClick }) {
  const a = f.activity;
  const timeLabel = useRelativeTime(f.activityStartedAt);
  const isSpotify = a?.type === 'spotify';
  const isGame    = a?.type === 'game';

  const accentColor = isSpotify ? '#1db954' : isGame ? '#5865f2' : '#06b6d4';
  const typeLabel   = isSpotify ? '🎵 Listening' : isGame ? '🎮 Playing' : '💻 Using';

  return (
    <div
      onClick={onClick}
      style={{ margin: '3px 4px', padding: '8px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: `1px solid ${accentColor}22`, cursor: 'pointer', transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar name={f.username || f.name} size={26} />
          <span style={{ position: 'absolute', bottom: -1, right: -1 }}>
            <StatusDot status={f.status} />
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {f.username || f.name}
          </div>
          <div style={{ fontSize: 9, color: accentColor, fontWeight: 600 }}>
            {typeLabel}{timeLabel ? ` · ${timeLabel} ago` : ''}
          </div>
        </div>
      </div>

      {/* Activity body */}
      <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 6, padding: '7px 8px' }}>
        {isSpotify ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{ width: 32, height: 32, borderRadius: 4, background: 'linear-gradient(135deg,#1db954,#158a3e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                {a.albumArt ? <img src={a.albumArt} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} /> : '🎵'}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.artist}</div>
                {a.album && <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.album}</div>}
              </div>
            </div>
            {a.duration > 0 && <ProgressBar elapsed={a.elapsed} duration={a.duration} />}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{ width: 32, height: 32, borderRadius: 4, background: 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
              {a?.largeIcon || '🎮'}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a?.name}</div>
              {a?.details && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.details}</div>}
              {a?.state && <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.state}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Simple friend row (no active activity) ────────────────────────────────────
const row = { display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', borderRadius: 6, cursor: 'pointer' };
const sectionLabel = { fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(255,255,255,0.35)', fontWeight: 700, padding: '6px 6px 3px' };

function FriendRow({ f, onClick, dimmed = false }) {
  const timeLabel = useRelativeTime(f.activityStartedAt);
  return (
    <div
      style={{ ...row, margin: '1px 4px', opacity: dimmed ? 0.5 : 1 }}
      onClick={onClick}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Avatar name={f.username || f.name} size={26} gradient={dimmed ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg,#06b6d4,#2563eb)'} />
        <span style={{ position: 'absolute', bottom: -1, right: -1 }}>
          <StatusDot status={dimmed ? 'offline' : f.status} />
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: dimmed ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {f.username || f.name || 'Unknown'}
        </div>
        {!dimmed && (
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {f.customStatus || STATUS_LABEL[f.status] || 'Online'}
            {timeLabel && !f.customStatus ? ` · ${timeLabel}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
const ActivityPanel = () => {
  const { friends, fetchAll } = useFriendStore();
  const getOnlineUsersArray = usePresenceStore(s => s.getOnlineUsersArray);
  const { onOpen }          = useModalStore();

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Convert Map → array safely
  const onlineUsers = getOnlineUsersArray ? getOnlineUsersArray() : [];

  const onlineIds = new Set(onlineUsers.map(u => u.id || u.userId));

  const onlineFriends  = friends.filter(f =>  onlineIds.has(f.id) || onlineIds.has(f.friendId));
  const offlineFriends = friends.filter(f => !onlineIds.has(f.id) && !onlineIds.has(f.friendId));

  const enriched = onlineFriends.map(f => {
    const presence = onlineUsers.find(u => u.id === f.id || u.id === f.friendId || u.userId === f.id || u.userId === f.friendId);
    return {
      ...f,
      status:            presence?.status           || 'online',
      activity:          presence?.activity         || null,
      customStatus:      presence?.customStatus     || null,
      activityStartedAt: presence?.activityStartedAt || null,
    };
  });

  // Demo: if no real friends, show the presence store users as standalone cards
  const demoUsers = friends.length === 0 ? onlineUsers.filter(u => u.status !== 'offline') : [];

  const withActivity    = enriched.filter(f => f.activity);
  const withoutActivity = enriched.filter(f => !f.activity);

  return (
    <div style={{ width: 200, flexShrink: 0, background: 'rgba(8,8,10,0.92)', borderLeft: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ height: 'var(--sz-header)', padding: '0 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'linear-gradient(135deg,#06b6d4,#3b82f6)', display: 'inline-block' }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>Active Now</span>
        {(onlineFriends.length > 0 || demoUsers.length > 0) && (
          <span style={{ marginLeft: 'auto', fontSize: 9, color: '#3ba55c', fontWeight: 700 }}>
            {onlineFriends.length || demoUsers.length} online
          </span>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>

        {/* Demo mode: show presence store users when no friends */}
        {demoUsers.length > 0 && (
          <>
            <div style={sectionLabel}>Live Activity</div>
            {demoUsers.filter(u => u.activity).map(u => (
              <ActivityCard
                key={u.id}
                f={{ ...u, username: u.username || u.name || u.id, name: u.username || u.name || u.id }}
                onClick={() => onOpen('userProfile', { member: u })}
              />
            ))}
            {demoUsers.filter(u => !u.activity).length > 0 && (
              <>
                <div style={{ ...sectionLabel, borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 4 }}>Online</div>
                {demoUsers.filter(u => !u.activity).map(u => (
                  <FriendRow key={u.id} f={{ ...u, username: u.name || u.id }} onClick={() => onOpen('userProfile', { member: u })} />
                ))}
              </>
            )}
          </>
        )}

        {/* Real friends with active rich presence */}
        {withActivity.length > 0 && (
          <>
            <div style={sectionLabel}>Live Activity</div>
            {withActivity.map(f => (
              <ActivityCard key={f.id} f={f} onClick={() => onOpen('userProfile', { member: f })} />
            ))}
          </>
        )}

        {/* Online friends without activity */}
        {withoutActivity.length > 0 && (
          <>
            <div style={{ ...sectionLabel, borderTop: withActivity.length > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none', marginTop: withActivity.length > 0 ? 4 : 0 }}>
              Friends — {enriched.length} Online
            </div>
            {withoutActivity.map(f => (
              <FriendRow key={f.id} f={f} onClick={() => onOpen('userProfile', { member: f })} />
            ))}
          </>
        )}

        {/* Offline (capped at 5) */}
        {offlineFriends.length > 0 && (
          <>
            <div style={{ ...sectionLabel, borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 4 }}>
              Offline — {offlineFriends.length}
            </div>
            {offlineFriends.slice(0, 5).map(f => (
              <FriendRow key={f.id} f={f} onClick={() => onOpen('userProfile', { member: f })} dimmed />
            ))}
          </>
        )}

        {/* True empty state */}
        {friends.length === 0 && demoUsers.length === 0 && (
          <div style={{ padding: '24px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>👥</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 1.5 }}>Add friends to see<br />what they're up to</div>
            <button
              onClick={() => onOpen('friends')}
              style={{ marginTop: 10, padding: '5px 12px', borderRadius: 6, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
            >
              Add Friends
            </button>
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div style={{ padding: '6px 8px', borderTop: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={() => onOpen('friends')}
          style={{ flex: 1, padding: '5px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', fontSize: 11, cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
        >
          <span>👥</span>
          <span>{friends.length} friend{friends.length !== 1 ? 's' : ''}</span>
        </button>
      </div>
    </div>
  );
};

export default ActivityPanel;
